import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Specy } from "types/Specy";
import AlertsContext from "common/providers/alerts";
import { BsFeather } from "react-icons/bs";
import usePark from "common/hooks/usePark";
import TagSelect from "components/select/TagSelect/TagSelect";
import SpecyItem from "./SpecyItem";

function Species() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageSpecies =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.species?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Specy>("species", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "Species_all",
  });
  const { removeItem } = useItem<Specy>("species", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/species/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.scientificName"),
        filterName: "scientificName$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/3",
      },
      {
        Header: t("forms.commonName"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/3",
      },

      {
        Header: t("forms.tag"),
        filterName: "tags",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.species")}
        subtitle={t("titleLegends.species")}
        btnTitle={t("newItem.specy")}
        btnSubtitle={t("newItem.specyLegend")}
        Icon={BsFeather}
        addLink="/species/add"
        addLinkDisabled={!canManageSpecies}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={SpecyItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Species;
